/**
 * ReceivingDetail.jsx — active-receipt scan / put-away flow.
 *
 * Extracted from Receiving.jsx (iter 102) so the list page stays focused on
 * filtering and the detail page owns its own complex scan state (manual entry,
 * continuous scan, batch scan, carton multipliers, sound toggle, unknown-
 * barcode match-it picker, and offline queueing).
 *
 * Props:
 *   - receipt         (object)   — the active receipt being processed
 *   - locations       (array)    — put-away locations available
 *   - variants        (array)    — all variants (for barcode lookup + picker)
 *   - skus            (array)    — for SKU-code labels in the UI
 *   - onExit          (fn)       — called when operator taps Exit
 *   - onReceiptRefresh (fn)      — called after any receive/complete/match so
 *                                  the parent can re-pull the receipts list.
 *                                  Receives the refreshed receipts array so
 *                                  the parent can update `activeReceipt` too.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { receiveItem, completeReceipt, getReceipts, getVariants, updateVariant } from '../lib/api';
import { enqueue as enqueueOffline } from '../lib/offlineQueue';
import { beep, isScanSoundEnabled, setScanSoundEnabled } from '../lib/scanBeep';
import { getCachedScanSettings } from '../lib/scanSettings';
import BarcodeScanner from './BarcodeScanner';
import UomPicker, { buildUomOptions } from './UomPicker';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Progress } from './ui/progress';
import useScanAnywhere from '../hooks/useScanAnywhere';
import {
  Barcode, CheckCircle, Camera, Lightning, Stack, X,
  SpeakerHigh, SpeakerSlash, Package, MagicWand, Robot,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ReceivingDetail({
  receipt,
  locations,
  variants: initialVariants,
  skus,
  onExit,
  onReceiptRefresh,
}) {
  // Local copy of `variants` so the "match it" flow can update the barcode list
  // without round-tripping through the parent.
  const [variants, setVariants] = useState(initialVariants);
  useEffect(() => { setVariants(initialVariants); }, [initialVariants]);

  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedVariant, setSelectedVariant] = useState('');
  const [receiveQty, setReceiveQty] = useState(1);
  // iter164 — Multi-UOM: operator picks which UOM the quantity is entered in.
  // Empty string = base UOM (no conversion sent). The picker auto-defaults
  // per selected variant; manual override is allowed.
  const [receiveUom, setReceiveUom] = useState('');
  // Scan-mode UOM — applies to every barcode scan (e.g. "I'm scanning cases").
  const [scanUom, setScanUom] = useState('');
  // Auto put-away suggestion — populated from /api/locations/best-fit
  // whenever (variant + qty) are known. Null when nothing fits.
  const [bestFit, setBestFit] = useState(null);
  // Batch put-away (Auto-allocate-all) state. The preview dialog opens once
  // we've fetched best-fit for every unreceived line; confirm then walks
  // those lines sequentially calling receive-item.
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchPreparing, setBatchPreparing] = useState(false);
  const [batchPlan, setBatchPlan] = useState([]); // [{variant_id, code, description, qty, location_id, candidates, no_fit}]
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0 });

  // Re-fetch best-fit suggestion whenever the operator picks a variant or
  // changes qty. Debounced trivially via the useEffect dependency-array
  // (re-runs only when actual values change). Aborts in-flight requests.
  useEffect(() => {
    if (!selectedVariant || !receiveQty || receiveQty < 1) {
      setBestFit(null);
      return;
    }
    const ctrl = new AbortController();
    (async () => {
      try {
        const r = await axios.get(`${API}/api/locations/best-fit`, {
          params: { variant_id: selectedVariant, quantity: receiveQty },
          withCredentials: true,
          signal: ctrl.signal,
        });
        setBestFit(r.data || null);
      } catch (_) { /* user navigated away */ }
    })();
    return () => ctrl.abort();
  }, [selectedVariant, receiveQty]);

  // Scan-to-count state
  const [inputMode, setInputMode] = useState('manual'); // 'manual' | 'scan'
  const [scanSubmitMode, setScanSubmitMode] = useState(() => getCachedScanSettings().default_submit_mode);
  const [cartonMultiplier, setCartonMultiplier] = useState(() => getCachedScanSettings().default_carton_multiplier);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [batchCounts, setBatchCounts] = useState({});
  const [unknownBarcode, setUnknownBarcode] = useState(null);
  const [matchPickVariantId, setMatchPickVariantId] = useState('');
  const [scanFeedback, setScanFeedback] = useState(null);
  const [soundOn, setSoundOn] = useState(() => isScanSoundEnabled());
  const scanFeedbackTimer = useRef(null);

  const receivedCount = receipt.received_items?.length || 0;
  const expectedCount = receipt.expected_items?.length || 1;
  const progress = (receivedCount / Math.max(expectedCount, 1)) * 100;

  // Build a per-variant tally of received quantities so the expected-items
  // checklist can show "rcv / expected" against each line. This lives in
  // memo so it only recomputes when received_items changes.
  const receivedByVariant = useMemo(() => {
    const m = {};
    for (const r of (receipt.received_items || [])) {
      if (!r?.variant_id) continue;
      m[r.variant_id] = (m[r.variant_id] || 0) + (parseInt(r.quantity, 10) || 0);
    }
    return m;
  }, [receipt.received_items]);

  // Stable expected-items list — fall back to [] so .map etc. is safe even
  // for legacy receipts created before this feature shipped.
  const expectedItems = receipt.expected_items || [];

  // Quantity-based progress (units expected vs units received) — gives a much
  // truer signal than line-count when a single line might be 1000 units.
  const expectedTotalQty = useMemo(
    () => expectedItems.reduce((s, it) => s + (parseInt(it.expected_qty, 10) || 0), 0),
    [expectedItems]
  );
  const receivedTotalQty = useMemo(
    () => Object.values(receivedByVariant).reduce((s, q) => s + q, 0),
    [receivedByVariant]
  );
  const qtyProgress = expectedTotalQty > 0
    ? Math.min(100, (receivedTotalQty / expectedTotalQty) * 100)
    : progress;

  // Pending lines = expected items with remaining qty > 0 AND a known
  // variant_id (we can't auto-allocate "unknown SKU" lines).
  const pendingLines = useMemo(() => expectedItems
    .filter((it) => it.variant_id)
    .map((it) => {
      const expected = parseInt(it.expected_qty, 10) || 0;
      const received = receivedByVariant[it.variant_id] || 0;
      const remaining = Math.max(0, expected - received);
      return { item: it, remaining };
    })
    .filter((row) => row.remaining > 0),
    [expectedItems, receivedByVariant]
  );

  // Auto-allocate all unreceived lines: ask /best-fit per line in parallel
  // (small N — usually <100), build a plan with the suggested location for
  // each, and open the preview dialog where ops can override before run.
  const openBatchPutaway = async () => {
    if (!pendingLines.length) {
      toast.message('Nothing to allocate — every line is already received.');
      return;
    }
    setBatchOpen(true);
    setBatchPreparing(true);
    setBatchPlan([]);
    try {
      const results = await Promise.all(pendingLines.map(async ({ item, remaining }) => {
        try {
          const r = await axios.get(`${API}/api/locations/best-fit`, {
            params: { variant_id: item.variant_id, quantity: remaining },
            withCredentials: true,
          });
          const v = variants.find((x) => x.id === item.variant_id);
          const sku = v && skusById[v.sku_id];
          return {
            variant_id: item.variant_id,
            code: item.sku_code || sku?.sku_code || v?.barcode || item.variant_id.slice(-8),
            description: item.description || sku?.name || '',
            qty: remaining,
            location_id: r.data?.best?.location_id || '',
            best: r.data?.best || null,
            candidates: r.data?.candidates || [],
            no_fit: !r.data?.best,
          };
        } catch (_) {
          return {
            variant_id: item.variant_id, code: item.sku_code || '?', description: '',
            qty: remaining, location_id: '', best: null, candidates: [], no_fit: true,
          };
        }
      }));
      setBatchPlan(results);
    } finally {
      setBatchPreparing(false);
    }
  };

  // Confirm: walk the plan sequentially and fire receiveItem for each line.
  // Sequential rather than Promise.all to avoid Mongo write-conflicts on the
  // same inventory row when the operator picked the same bin for multiple
  // lines, and so progress reads naturally (1/N → 2/N).
  const runBatchPutaway = async () => {
    const lines = batchPlan.filter((p) => p.location_id && !p.no_fit);
    if (!lines.length) {
      toast.error('No lines have a put-away location selected.');
      return;
    }
    setBatchRunning(true);
    setBatchProgress({ done: 0, total: lines.length });
    let success = 0;
    let failed = 0;
    let total_units = 0;
    for (const line of lines) {
      try {
        const payload = {
          variant_id: line.variant_id,
          location_id: line.location_id,
          quantity: line.qty,
        };
        // iter164 — pass line-level UOM if operator picked one in the plan UI
        if (line.uom) payload.uom = line.uom;
        await receiveItem(receipt.id, payload);
        success += 1;
        total_units += line.qty;
      } catch (e) {
        failed += 1;
        setBatchPlan((prev) => prev.map((p) =>
          p.variant_id === line.variant_id
            ? { ...p, error: e?.response?.data?.detail || 'Failed' }
            : p));
      }
      setBatchProgress((p) => ({ ...p, done: p.done + 1 }));
    }
    setBatchRunning(false);
    if (failed === 0) {
      toast.success(`Auto put-away · ${success} line${success === 1 ? '' : 's'} (${total_units} units) received`);
      setBatchOpen(false);
      setBatchPlan([]);
    } else {
      toast.error(`Put-away done with errors · ${success} ok / ${failed} failed — review and retry`);
    }
    // Refresh receipts via the existing helper so onReceiptRefresh receives
    // the full array shape the parent expects. Calling onReceiptRefresh()
    // bare here triggered an "undefined.find" crash before fix.
    await refreshReceipts();
  };

  const variantsByBarcode = useMemo(() => {
    const m = {};
    for (const v of variants) {
      if (v.barcode) m[String(v.barcode).toUpperCase()] = v;
    }
    return m;
  }, [variants]);
  const skusById = useMemo(() => Object.fromEntries(skus.map((s) => [s.id, s])), [skus]);

  // Lookup map for the manual entry box. Lets warehouse staff type EITHER
  // the SKU code (printed on most case labels) OR the variant barcode OR
  // the last-8 chars of the variant id. Same map also serves RF scanners
  // that emulate a keyboard — they paste a code + Enter, we resolve it
  // against this map and behave exactly like a camera scan.
  const variantsByCode = useMemo(() => {
    const m = {};
    for (const v of variants) {
      if (v.barcode) m[String(v.barcode).toUpperCase()] = v;
      if (v.id) m[String(v.id).slice(-8).toUpperCase()] = v;
      const sku = skusById[v.sku_id];
      if (sku?.sku_code) {
        // Multiple variants can share an SKU code — only assign the first
        // one we see so a typed sku_code resolves predictably. Operators
        // who need a non-default variant can still use the dropdown.
        const key = String(sku.sku_code).toUpperCase();
        if (!m[key]) m[key] = v;
      }
    }
    return m;
  }, [variants, skusById]);
  const [manualCode, setManualCode] = useState('');

  const flashScanFeedback = (type, message, ms = 900) => {
    setScanFeedback({ type, message });
    beep(type === 'ok' ? 'ok' : type === 'warn' ? 'warn' : 'err');
    if (scanFeedbackTimer.current) clearTimeout(scanFeedbackTimer.current);
    scanFeedbackTimer.current = setTimeout(() => setScanFeedback(null), ms);
  };

  // Helper — refresh parent's receipts list
  const refreshReceipts = async () => {
    try {
      const res = await getReceipts();
      onReceiptRefresh?.(res.data);
    } catch { /* ignore */ }
  };

  // Resolve a typed-or-RF-scanned code → variant id, drop it into the form.
  // Used by the manual-entry tab below; lives here so it can also be wired
  // to a global RF-scanner key listener later if we want frame-less scan.
  const resolveTypedCode = (raw) => {
    const code = String(raw || '').trim().toUpperCase();
    if (!code) return null;
    const variant = variantsByCode[code];
    if (variant) {
      setSelectedVariant(variant.id);
      setManualCode('');
      const skuLabel = skusById[variant.sku_id]?.sku_code || variant.barcode || code;
      flashScanFeedback('ok', `✓ ${skuLabel}`);
      return variant;
    }
    flashScanFeedback('err', `Unknown · ${code}`);
    // Reuse the existing "match unknown barcode" dialog so warehouse staff
    // can teach the system about a new barcode without leaving the page.
    setUnknownBarcode({ barcode: code });
    setMatchPickVariantId('');
    return null;
  };

  // Scan-anywhere — enables global keyboard-burst capture so an RF gun
  // pull anywhere on the page lands a movement, even when no input is
  // focused. Disabled when the camera scanner is open (it owns the input
  // surface) or when the unknown-barcode-match dialog is up (the scan
  // already happened and we're waiting for the human to teach us the
  // mapping). Tied to manual mode only — scan-mode has its own loop.
  const scanAnywhereActive = inputMode === 'manual' && !scannerOpen && !unknownBarcode;
  useScanAnywhere(scanAnywhereActive, resolveTypedCode);

  const handleReceive = async () => {
    if (!selectedLocation || !selectedVariant) return;
    const payload = { variant_id: selectedVariant, location_id: selectedLocation, quantity: receiveQty };
    // iter164 — only send `uom` when it differs from the SKU's base unit;
    // backend treats absent uom as base, which is the legacy behaviour.
    const sv = variants.find((v) => v.id === selectedVariant);
    const skuForUom = sv && skusById[sv.sku_id];
    const baseUom = (skuForUom?.unit_of_measure || 'each').toLowerCase();
    if (receiveUom && receiveUom !== baseUom) payload.uom = receiveUom;
    const offlineQueue = () => {
      enqueueOffline({
        path: `/api/receipts/${receipt.id}/receive-item`,
        method: 'POST',
        body: payload,
        label: `Receive · ${String(receipt.id).slice(0, 8)}`,
      });
    };
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      offlineQueue();
      toast.message('Offline — receive queued. Will sync when back online.');
      setSelectedVariant(''); setReceiveQty(1);
      return;
    }
    try {
      const res = await receiveItem(receipt.id, payload);
      const av = res?.data?.availability;
      // Build a tiny verification toast showing operator the actual
      // before→after delta. When auto-release consumed some/all of the
      // received qty, surface it explicitly so the unchanged availability
      // doesn't look like a silent failure.
      if (av && typeof av.received_qty === 'number') {
        const variantDoc = variants.find((v) => v.id === selectedVariant);
        const skuLabel = (variantDoc && skusById[variantDoc.sku_id]?.sku_code)
          || variantDoc?.barcode
          || (selectedVariant || '').slice(0, 8);
        const arrow = `${av.before} → ${av.after}`;
        const sign = av.delta > 0 ? `+${av.delta}` : `${av.delta}`;
        const released = Number(av.auto_released_to_backorders || 0);
        const main = `Received ${av.received_qty} · ${skuLabel} availability ${arrow} (${sign})`;
        if (released > 0) {
          toast.success(main, {
            description: `${released} unit${released === 1 ? '' : 's'} auto-shipped to fulfill backorders`,
            duration: 6000,
          });
        } else if (av.delta === 0 && av.received_qty > 0) {
          // No backorders consumed it but availability didn't move — almost
          // certainly a warehouse_id/scope mismatch. Warn the operator.
          toast.warning(main, {
            description: 'Receive logged but picker availability did not move — check warehouse / location scope',
            duration: 8000,
          });
        } else {
          toast.success(main);
        }
      }
      await refreshReceipts();
      setSelectedVariant(''); setReceiveQty(1);
    } catch (e) {
      if (!navigator.onLine || e?.message === 'Network Error') {
        offlineQueue();
        toast.message('Connection lost — receive queued. Will sync when back online.');
        setSelectedVariant(''); setReceiveQty(1);
      } else {
        console.error(e);
      }
    }
  };

  const handleComplete = async (extras) => {
    try {
      await completeReceipt(receipt.id, extras || {});
      onExit?.();
      await refreshReceipts();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Could not complete receipt');
    }
  };

  // Total outstanding units across the whole receipt — drives the
  // Complete-with-shortage dialog (skipped when nothing is short).
  const totalOutstanding = useMemo(
    () => expectedItems.reduce((s, it) => {
      const exp = parseInt(it.expected_qty, 10) || 0;
      const rcv = receivedByVariant[it.variant_id] || 0;
      return s + Math.max(0, exp - rcv);
    }, 0),
    [expectedItems, receivedByVariant],
  );
  const shortLineCount = useMemo(
    () => expectedItems.filter((it) => {
      const exp = parseInt(it.expected_qty, 10) || 0;
      const rcv = receivedByVariant[it.variant_id] || 0;
      return exp > rcv;
    }).length,
    [expectedItems, receivedByVariant],
  );

  // Complete-with-shortage dialog state
  const [shortageOpen, setShortageOpen] = useState(false);
  const [shortageReason, setShortageReason] = useState('');
  const [shortageNotes, setShortageNotes] = useState('');
  const [shortageSubmitting, setShortageSubmitting] = useState(false);

  const onCompleteTap = () => {
    if (totalOutstanding > 0) {
      setShortageOpen(true);
    } else {
      handleComplete();
    }
  };

  const submitShortage = async () => {
    if (!shortageReason) {
      toast.error('Pick a shortage reason — needed for supplier scorecards.');
      return;
    }
    setShortageSubmitting(true);
    try {
      await handleComplete({
        shortage_reason: shortageReason,
        shortage_notes: shortageNotes,
      });
      setShortageOpen(false);
    } finally {
      setShortageSubmitting(false);
    }
  };

  const postReceiveOne = async (variantId) => {
    if (!selectedLocation) return false;
    const qty = Math.max(1, parseInt(cartonMultiplier, 10) || 1);
    const payload = { variant_id: variantId, location_id: selectedLocation, quantity: qty };
    // iter164 — scan-mode UOM: when set, every scan converts at that pack-size.
    const sv = variants.find((v) => v.id === variantId);
    const skuForUom = sv && skusById[sv.sku_id];
    const baseUom = (skuForUom?.unit_of_measure || 'each').toLowerCase();
    if (scanUom && scanUom !== baseUom) {
      // Only attach uom when the selected scan-UOM exists on this SKU.
      // Mixed-SKU scanning where some SKUs lack the UOM falls back to base.
      const opts = buildUomOptions(skuForUom).map((o) => o.code);
      if (opts.includes(scanUom)) payload.uom = scanUom;
    }
    const offlineQueue = () => enqueueOffline({
      path: `/api/receipts/${receipt.id}/receive-item`,
      method: 'POST',
      body: payload,
      label: `Receive · ${String(receipt.id).slice(0, 8)}`,
    });
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      offlineQueue();
      return true;
    }
    try {
      const res = await receiveItem(receipt.id, payload);
      // Scan-to-count flow uses brief flashScanFeedback rather than a full
      // toast — keep that path snappy. Surface a warning only when delta=0
      // with no backorder consumption, which signals a silent mismatch.
      const av = res?.data?.availability;
      if (av && av.received_qty > 0 && av.delta === 0
          && !Number(av.auto_released_to_backorders || 0)) {
        flashScanFeedback('warn', `Receive logged · avail ${av.before} → ${av.after} (no change)`);
      }
      return true;
    } catch (e) {
      if (!navigator.onLine || e?.message === 'Network Error') {
        offlineQueue();
        return true;
      }
      console.error(e);
      return false;
    }
  };

  const handleScan = async (rawCode) => {
    const code = String(rawCode || '').trim().toUpperCase();
    if (!code) return;
    if (!selectedLocation) {
      flashScanFeedback('warn', 'Pick a put-away location first');
      return;
    }
    const variant = variantsByBarcode[code];
    if (!variant) {
      setScannerOpen(false);
      setUnknownBarcode({ barcode: code });
      setMatchPickVariantId('');
      return;
    }
    if (scanSubmitMode === 'batch') {
      const n = Math.max(1, parseInt(cartonMultiplier, 10) || 1);
      setBatchCounts((prev) => ({ ...prev, [variant.id]: (prev[variant.id] || 0) + n }));
      flashScanFeedback('ok', `+${n} · ${skusById[variant.sku_id]?.sku_code || variant.barcode}`);
      return;
    }
    // Continuous — fire and forget
    const ok = await postReceiveOne(variant.id);
    if (ok) {
      const n = Math.max(1, parseInt(cartonMultiplier, 10) || 1);
      flashScanFeedback('ok', `✓ ${skusById[variant.sku_id]?.sku_code || variant.barcode}${n > 1 ? ` ×${n}` : ''}`);
      if (navigator.onLine) await refreshReceipts();
    } else {
      flashScanFeedback('err', 'Submit failed');
    }
  };

  const submitBatch = async () => {
    if (!selectedLocation) return;
    const entries = Object.entries(batchCounts).filter(([, q]) => q > 0);
    if (entries.length === 0) return;
    let ok = 0; let failed = 0;
    for (const [variantId, qty] of entries) {
      const payload = { variant_id: variantId, location_id: selectedLocation, quantity: qty };
      // iter164 — apply scan-mode UOM per line when valid for that SKU
      const sv = variants.find((v) => v.id === variantId);
      const skuForUom = sv && skusById[sv.sku_id];
      const baseUom = (skuForUom?.unit_of_measure || 'each').toLowerCase();
      if (scanUom && scanUom !== baseUom) {
        const opts = buildUomOptions(skuForUom).map((o) => o.code);
        if (opts.includes(scanUom)) payload.uom = scanUom;
      }
      try {
        await receiveItem(receipt.id, payload);
        ok++;
      } catch (e) {
        if (!navigator.onLine || e?.message === 'Network Error') {
          enqueueOffline({
            path: `/api/receipts/${receipt.id}/receive-item`,
            method: 'POST', body: payload,
            label: `Receive · ${String(receipt.id).slice(0, 8)}`,
          });
          ok++;
        } else {
          failed++;
        }
      }
    }
    setBatchCounts({});
    if (failed > 0) toast.error(`Submitted ${ok} lines, ${failed} failed`);
    else toast.success(`${ok} lines submitted`);
    await refreshReceipts();
  };

  const confirmUnknownMatch = async () => {
    if (!unknownBarcode || !matchPickVariantId) return;
    try {
      await updateVariant(matchPickVariantId, { barcode: unknownBarcode.barcode });
      const v = await getVariants();
      setVariants(v.data);
      const matched = v.data.find((x) => x.id === matchPickVariantId);
      if (matched) {
        const n = Math.max(1, parseInt(cartonMultiplier, 10) || 1);
        if (scanSubmitMode === 'batch') {
          setBatchCounts((prev) => ({ ...prev, [matched.id]: (prev[matched.id] || 0) + n }));
          flashScanFeedback('ok', `Matched & +${n} · ${skusById[matched.sku_id]?.sku_code || matched.barcode}`);
        } else {
          const ok = await postReceiveOne(matched.id);
          flashScanFeedback(ok ? 'ok' : 'err', ok
            ? `Matched & received · ${skusById[matched.sku_id]?.sku_code || matched.barcode}${n > 1 ? ` ×${n}` : ''}`
            : 'Matched but submit failed');
        }
      }
      setUnknownBarcode(null);
      setMatchPickVariantId('');
      setScannerOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Match failed');
    }
  };

  return (
    <>
      <div data-testid="receiving-active" className="p-4 md:p-6 max-w-2xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-bold">{receipt.receipt_number}</h1>
            <Button variant="outline" onClick={onExit}>Exit</Button>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Supplier: {receipt.supplier_name}</span>
            {receipt.po_number && <span>• PO: {receipt.po_number}</span>}
          </div>
          <Progress value={qtyProgress} className="h-2 mt-2" />
          {expectedTotalQty > 0 && (
            <p
              className="text-[11px] text-muted-foreground mt-1 font-mono tabular-nums"
              data-testid="receipt-progress-summary"
            >
              {receivedTotalQty} / {expectedTotalQty} units · {expectedItems.length} expected line{expectedItems.length === 1 ? '' : 's'}
            </p>
          )}
        </div>

        {expectedItems.length > 0 && (
          <div
            className="bg-card border border-border rounded-lg p-4 mb-4"
            data-testid="expected-checklist"
          >
            <p className="overline mb-3 flex items-center justify-between">
              <span>Expected Items · tick as you receive</span>
              <span className="font-mono normal-case tracking-normal text-muted-foreground">
                {expectedItems.filter((it) => (receivedByVariant[it.variant_id] || 0) >= (it.expected_qty || 0)).length}
                {' '}/ {expectedItems.length} complete
              </span>
            </p>
            {/* Auto-allocate-all — opens the batch put-away preview where
                each unreceived line gets a best-fit suggestion. Disabled
                when nothing is pending. */}
            {pendingLines.length > 0 && (
              <div className="mb-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={openBatchPutaway}
                  data-testid="batch-putaway-open"
                  className="w-full bg-blue-50 hover:bg-blue-100 border-blue-300 text-blue-900 font-bold uppercase tracking-widest text-xs"
                >
                  <Robot size={14} weight="bold" className="mr-1.5" />
                  Auto-allocate all ({pendingLines.length} pending line{pendingLines.length === 1 ? '' : 's'})
                </Button>
              </div>
            )}
            <div className="space-y-1.5">
              {expectedItems.map((it, idx) => {
                const rcv = receivedByVariant[it.variant_id] || 0;
                const exp = parseInt(it.expected_qty, 10) || 0;
                const isComplete = exp > 0 && rcv >= exp;
                const isOver = exp > 0 && rcv > exp;
                const isShort = exp > 0 && rcv > 0 && rcv < exp;
                const remaining = Math.max(0, exp - rcv);
                const pct = exp > 0 ? Math.min(100, (rcv / exp) * 100) : 0;
                const v = it.variant_id ? variants.find((x) => x.id === it.variant_id) : null;
                const sku = v && skusById[v.sku_id];
                const code = it.sku_code || sku?.sku_code || v?.barcode || (it.variant_id ? it.variant_id.slice(-8) : '—');
                const description = it.description || sku?.name || '';
                const variantSuffix = [v?.size || it.size, v?.color || it.color].filter(Boolean).join(' · ');

                const handleTap = () => {
                  if (!it.variant_id) return;
                  setInputMode('manual');
                  setSelectedVariant(it.variant_id);
                  // Pre-fill remaining qty so a single tap "Receive Item" empties the line.
                  if (remaining > 0) setReceiveQty(remaining);
                };

                return (
                  <button
                    key={`${it.variant_id || 'x'}-${idx}`}
                    type="button"
                    onClick={handleTap}
                    data-testid={`expected-row-${code}`}
                    className={`w-full text-left border rounded-md p-2.5 transition flex items-center gap-3 ${
                      isOver ? 'border-amber-300 bg-amber-50 hover:bg-amber-100' :
                      isComplete ? 'border-emerald-300 bg-emerald-50 hover:bg-emerald-100' :
                      isShort ? 'border-blue-200 bg-blue-50 hover:bg-blue-100' :
                      'border-border hover:bg-muted'
                    }`}
                  >
                    <div
                      aria-hidden
                      className={`h-5 w-5 shrink-0 rounded-full border-2 flex items-center justify-center transition ${
                        isComplete ? 'bg-emerald-600 border-emerald-600 text-white' :
                        isOver ? 'bg-amber-500 border-amber-500 text-white' :
                        isShort ? 'border-blue-500' : 'border-muted-foreground/40'
                      }`}
                    >
                      {(isComplete || isOver) && <CheckCircle size={14} weight="fill" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="font-mono font-bold truncate">{code}</span>
                        <span
                          className={`font-mono text-sm tabular-nums shrink-0 ${
                            isOver ? 'text-amber-700' :
                            isComplete ? 'text-emerald-700' :
                            'text-foreground'
                          }`}
                          data-testid={`expected-progress-${code}`}
                        >
                          {rcv} / {exp}
                          {isOver && <span className="ml-1 text-[10px] uppercase font-bold">+{rcv - exp} over</span>}
                        </span>
                      </div>
                      {(description || variantSuffix) && (
                        <div className="text-xs text-muted-foreground truncate">
                          {description}{description && variantSuffix ? ' · ' : ''}{variantSuffix}
                        </div>
                      )}
                      {exp > 0 && (
                        <div className="mt-1 h-1 w-full bg-muted rounded">
                          <div
                            className={`h-1 rounded ${isOver ? 'bg-amber-500' : isComplete ? 'bg-emerald-600' : 'bg-brand'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {receipt.received_items?.length > 0 && (
          <div className="bg-green-50 border border-green-200 p-4 mb-6">
            <p className="overline mb-2 text-green-700">Received Items ({receivedCount})</p>
            <div className="space-y-2">
              {receipt.received_items.map((item, i) => (
                <div key={i} className="flex items-center justify-between text-green-700 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={16} weight="fill" />
                    <span className="font-mono">{item.variant_id?.slice(-8)}</span>
                  </div>
                  <span className="font-bold">x{item.quantity}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-1 mb-4 p-1 bg-muted rounded-md" data-testid="input-mode-tabs">
          <button
            type="button"
            onClick={() => setInputMode('manual')}
            data-testid="mode-tab-manual"
            className={`py-2 text-xs font-bold uppercase tracking-wider rounded transition ${
              inputMode === 'manual' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >Manual entry</button>
          <button
            type="button"
            onClick={() => setInputMode('scan')}
            data-testid="mode-tab-scan"
            className={`py-2 text-xs font-bold uppercase tracking-wider rounded transition flex items-center justify-center gap-1.5 ${
              inputMode === 'scan' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          ><Camera size={14} weight="fill" /> Scan to count</button>
        </div>

        <div className="bg-card border border-border rounded-lg p-5 space-y-4">
          <div>
            <Label className="overline">Put-Away Location</Label>
            <Select value={selectedLocation} onValueChange={setSelectedLocation}>
              <SelectTrigger className="mt-1" data-testid="put-away-location"><SelectValue placeholder="Select location" /></SelectTrigger>
              <SelectContent>
                {locations.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>{loc.location_code} ({loc.current_occupancy}/{loc.capacity})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Best-fit suggestion — appears once a variant + qty are picked.
                Single-tap "Use it" applies the bin without opening the
                dropdown. Renders only when the suggestion DIFFERS from the
                already-selected location and the bin has capacity to fit. */}
            {bestFit?.best && bestFit.best.location_id !== selectedLocation && (
              <div
                data-testid="best-fit-suggestion"
                className={`mt-2 px-3 py-2 border flex items-start gap-2 text-xs ${
                  bestFit.best.consolidation
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                    : 'bg-blue-50 border-blue-200 text-blue-900'
                }`}
              >
                <MagicWand size={14} weight="fill" className="mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-bold uppercase tracking-widest text-[10px]">
                    {bestFit.best.consolidation ? 'Best fit · consolidate' : 'Best fit · empty rack'}
                  </div>
                  <div className="font-mono mt-0.5">
                    {bestFit.best.location_code}
                    <span className="text-muted-foreground ml-2 normal-case tracking-normal">
                      {bestFit.best.consolidation
                        ? `already has ${bestFit.best.same_variant_qty} units of this SKU`
                        : `${bestFit.best.percent_full_before}% full → ${bestFit.best.percent_full_after}% after put-away`}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedLocation(bestFit.best.location_id)}
                  data-testid="best-fit-apply"
                  className="shrink-0 px-2 py-1 text-[10px] font-bold uppercase tracking-widest bg-foreground text-background hover:opacity-90"
                >
                  Use it
                </button>
              </div>
            )}
            {bestFit && !bestFit.best && selectedVariant && (
              <p data-testid="best-fit-no-fit" className="mt-2 text-[10px] text-amber-700">
                No bin with enough remaining volume — pick manually or split the receive.
              </p>
            )}
          </div>

          {inputMode === 'manual' ? (
            <>
              <div>
                <Label className="overline">Type SKU / Barcode · or RF Scan</Label>
                <Input
                  type="text"
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      resolveTypedCode(manualCode);
                    }
                  }}
                  placeholder="Type SKU code, barcode, or scan with RF gun + Enter"
                  className="mt-1 font-mono uppercase tracking-wider"
                  autoFocus
                  data-testid="manual-sku-input"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Tip: warehouse RF guns emulate a keyboard — point and pull, the SKU lands here automatically.
                  Press <kbd className="px-1 border border-border rounded text-[10px]">Enter</kbd> after typing
                  to resolve. Unknown codes prompt you to map them to a variant.
                </p>
                {scanAnywhereActive && (
                  <div className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-emerald-700"
                    data-testid="scan-anywhere-indicator"
                    title="Pull your RF gun anywhere on this page — keystrokes will route to the resolver automatically.">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                    </span>
                    RF scan-anywhere armed
                  </div>
                )}
              </div>
              <div>
                <Label className="overline">Variant <span className="text-muted-foreground font-normal lowercase tracking-normal">— or browse the full list</span></Label>
                <Select value={selectedVariant} onValueChange={setSelectedVariant}>
                  <SelectTrigger className="mt-1" data-testid="manual-variant-select"><SelectValue placeholder="Pick a variant…" /></SelectTrigger>
                  <SelectContent>
                    {variants.map((v) => {
                      const sku = skusById[v.sku_id];
                      const skuCode = sku?.sku_code || '—';
                      const meta = [v.size, v.color].filter(Boolean).join(' · ');
                      return (
                        <SelectItem key={v.id} value={v.id}>
                          <span className="font-mono font-semibold">{skuCode}</span>
                          {v.barcode && <span className="text-muted-foreground"> · {v.barcode}</span>}
                          {meta && <span className="text-muted-foreground"> · {meta}</span>}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {selectedVariant && (() => {
                  const v = variants.find((x) => x.id === selectedVariant);
                  if (!v) return null;
                  const sku = skusById[v.sku_id];
                  return (
                    <div className="mt-1 text-xs bg-emerald-50 border border-emerald-200 px-2 py-1 flex items-center gap-2"
                      data-testid="manual-resolved-variant">
                      <span className="text-emerald-700 font-bold">✓ Resolved</span>
                      <span className="font-mono font-semibold">{sku?.sku_code || '—'}</span>
                      {v.barcode && <span className="text-muted-foreground">· {v.barcode}</span>}
                    </div>
                  );
                })()}
              </div>
              <div>
                <Label className="overline">Quantity</Label>
                <div className="mt-1 flex gap-2 items-center">
                  <Input
                    type="number"
                    value={receiveQty}
                    onChange={(e) => setReceiveQty(parseFloat(e.target.value) || 0)}
                    min={0}
                    step="any"
                    className="flex-1"
                    data-testid="manual-receive-qty"
                  />
                  {(() => {
                    const v = variants.find((x) => x.id === selectedVariant);
                    const sku = v && skusById[v.sku_id];
                    return (
                      <UomPicker
                        sku={sku}
                        value={receiveUom || (sku?.unit_of_measure || '').toLowerCase()}
                        onChange={(code) => setReceiveUom(code)}
                        testId="manual-receive-uom"
                      />
                    );
                  })()}
                </div>
                {/* iter164 — Live conversion preview */}
                {(() => {
                  const v = variants.find((x) => x.id === selectedVariant);
                  const sku = v && skusById[v.sku_id];
                  if (!sku || !receiveUom) return null;
                  const baseUom = (sku.unit_of_measure || 'each').toLowerCase();
                  if (receiveUom === baseUom) return null;
                  const conv = (sku.uom_conversions || []).find((r) => (r.code || '').toLowerCase() === receiveUom);
                  if (!conv) return null;
                  const total = (parseFloat(receiveQty) || 0) * (parseFloat(conv.factor) || 1);
                  return (
                    <p className="text-[11px] text-emerald-700 mt-1 font-mono" data-testid="manual-receive-conversion">
                      = {total} {baseUom} <span className="opacity-60">(base units)</span>
                    </p>
                  );
                })()}
              </div>
              <Button onClick={handleReceive} data-testid="receive-item-btn" className="w-full h-14 text-lg bg-brand hover:bg-brand/90 font-bold">
                <Barcode size={24} className="mr-2" /> Receive Item
              </Button>
            </>
          ) : (
            <>
              <div>
                <Label className="overline">Scan submit mode</Label>
                <div className="grid grid-cols-2 gap-1 mt-1 p-1 bg-muted rounded-md" data-testid="scan-submit-mode">
                  <button
                    type="button"
                    onClick={() => setScanSubmitMode('continuous')}
                    data-testid="submit-mode-continuous"
                    className={`py-2 text-[11px] font-bold uppercase tracking-wider rounded transition flex items-center justify-center gap-1 ${
                      scanSubmitMode === 'continuous' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  ><Lightning size={12} weight="fill" /> Continuous</button>
                  <button
                    type="button"
                    onClick={() => setScanSubmitMode('batch')}
                    data-testid="submit-mode-batch"
                    className={`py-2 text-[11px] font-bold uppercase tracking-wider rounded transition flex items-center justify-center gap-1 ${
                      scanSubmitMode === 'batch' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  ><Stack size={12} weight="fill" /> Batch</button>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1.5">
                  {scanSubmitMode === 'continuous'
                    ? 'Each scan instantly POSTs +N. Best when on Wi-Fi.'
                    : 'Scans accumulate locally. Tap Submit when done — best for offline.'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="overline flex items-center gap-1">
                    <Package size={10} weight="fill" /> Carton qty
                  </Label>
                  <Input
                    type="number" min={1} max={9999} inputMode="numeric"
                    value={cartonMultiplier}
                    onChange={(e) => setCartonMultiplier(Math.max(1, parseInt(e.target.value, 10) || 1))}
                    className="mt-1 font-mono text-base"
                    data-testid="carton-multiplier-input"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Each scan = +{cartonMultiplier}{scanUom ? ` ${scanUom}` : ' unit' + (cartonMultiplier === 1 ? '' : 's')}
                  </p>
                </div>
                <div>
                  <Label className="overline">Scan UOM <span className="font-normal normal-case text-muted-foreground">(optional)</span></Label>
                  <Input
                    type="text"
                    value={scanUom}
                    placeholder="case / L / kg…"
                    onChange={(e) => setScanUom(e.target.value.trim().toLowerCase())}
                    className="mt-1 font-mono uppercase"
                    maxLength={24}
                    data-testid="scan-uom-input"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Per-scan UOM. Falls back to each SKU&apos;s base when not configured.
                  </p>
                </div>
              </div>

              <div>
                <Label className="overline">Scan sound</Label>
                <button
                  type="button"
                  onClick={() => { const next = !soundOn; setSoundOn(next); setScanSoundEnabled(next); if (next) beep('ok'); }}
                  data-testid="scan-sound-toggle"
                  aria-pressed={soundOn}
                  className={`mt-1 w-full h-10 rounded-md border flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-wider transition ${
                    soundOn ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-muted border-border text-muted-foreground'
                  }`}
                >
                  {soundOn ? <SpeakerHigh size={14} weight="fill" /> : <SpeakerSlash size={14} weight="fill" />}
                  {soundOn ? 'On' : 'Muted'}
                </button>
                <p className="text-[10px] text-muted-foreground mt-1">Audio chirp on each scan</p>
              </div>

              {scanSubmitMode === 'batch' && Object.keys(batchCounts).length > 0 && (
                <div className="border border-border rounded-md p-3 space-y-2" data-testid="batch-counter">
                  <p className="overline">Pending batch</p>
                  {Object.entries(batchCounts).map(([vid, qty]) => {
                    const v = variants.find((x) => x.id === vid);
                    const sku = v && skusById[v.sku_id];
                    return (
                      <div key={vid} className="flex items-center justify-between text-sm">
                        <span className="font-mono truncate">
                          {sku?.sku_code || v?.barcode || vid.slice(-8)}
                          {v?.size ? ` · ${v.size}` : ''}{v?.color ? ` · ${v.color}` : ''}
                        </span>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="font-mono font-bold tabular-nums">×{qty}</span>
                          <button
                            onClick={() => setBatchCounts((p) => { const n = { ...p }; delete n[vid]; return n; })}
                            className="p-1 hover:bg-muted rounded text-muted-foreground"
                            aria-label="Remove from batch"
                          ><X size={14} /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <Button
                onClick={() => {
                  if (!selectedLocation) { toast.error('Pick a put-away location first'); return; }
                  setScannerOpen(true);
                }}
                data-testid="open-scanner-btn"
                className="w-full h-14 text-lg bg-brand hover:bg-brand/90 font-bold"
              >
                <Camera size={24} className="mr-2" weight="fill" /> Open scanner
              </Button>

              {scanSubmitMode === 'batch' && Object.keys(batchCounts).length > 0 && (
                <Button
                  onClick={submitBatch}
                  data-testid="submit-batch-btn"
                  className="w-full h-12 text-base bg-emerald-600 hover:bg-emerald-700 font-bold"
                >
                  <CheckCircle size={20} className="mr-2" /> Submit batch ({Object.values(batchCounts).reduce((a, b) => a + b, 0)} units)
                </Button>
              )}
            </>
          )}

          {receivedCount > 0 && (
            <Button
              onClick={onCompleteTap}
              variant="outline"
              data-testid="complete-receipt-btn"
              className={`w-full h-14 text-lg font-bold ${
                totalOutstanding > 0
                  ? 'text-amber-700 border-amber-300 hover:bg-amber-50'
                  : 'text-green-600 border-green-200 hover:bg-green-50'
              }`}
            >
              <CheckCircle size={24} className="mr-2" />
              {totalOutstanding > 0
                ? `Complete · ${totalOutstanding} short`
                : 'Complete Receipt'}
            </Button>
          )}
        </div>
      </div>

      {scannerOpen && (
        <BarcodeScanner
          title={
            scanSubmitMode === 'batch'
              ? `Batch · ${Object.values(batchCounts).reduce((a, b) => a + b, 0)} units · ${receipt.receipt_number}`
              : `${receipt.received_items?.length || 0} received · ${receipt.receipt_number}`
          }
          onScan={async (code) => {
            setScannerOpen(false);
            await handleScan(code);
            if (!unknownBarcode) setTimeout(() => setScannerOpen(true), 300);
          }}
          onClose={() => setScannerOpen(false)}
        />
      )}

      {scanFeedback && (
        <div
          className={`fixed top-20 left-1/2 -translate-x-1/2 z-[80] px-4 py-2 rounded-full shadow-lg text-sm font-bold uppercase tracking-wider ${
            scanFeedback.type === 'ok' ? 'bg-emerald-600 text-white' : 'bg-rose-600 text-white'
          }`}
          data-testid="scan-feedback"
        >{scanFeedback.message}</div>
      )}

      {/* Complete-with-shortage dialog. Surfaces when ops taps the
          "Complete · N short" button. Captures a reason (required) plus
          optional notes so supplier scorecards + claim workflows can
          consume it. */}
      <Dialog open={shortageOpen} onOpenChange={setShortageOpen}>
        <DialogContent className="border border-border rounded-none" data-testid="shortage-dialog">
          <DialogHeader>
            <DialogTitle className="font-mono">
              Close with shortage · {totalOutstanding} unit{totalOutstanding === 1 ? '' : 's'} short
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-300 px-3 py-2 text-xs text-amber-900">
              <p className="font-bold uppercase tracking-widest text-[10px] mb-1">Heads-up</p>
              <p>
                You're closing this PO with <span className="font-bold tabular-nums">{totalOutstanding}</span> unit
                {totalOutstanding === 1 ? '' : 's'} still outstanding across{' '}
                <span className="font-bold tabular-nums">{shortLineCount}</span> line
                {shortLineCount === 1 ? '' : 's'}. Outstanding stock will <strong>not</strong> remain on this receipt —
                if you're expecting a back-order delivery, leave the PO open instead and resume later.
              </p>
            </div>
            <div>
              <Label className="overline" htmlFor="shortage-reason-select">Reason</Label>
              <Select value={shortageReason} onValueChange={setShortageReason}>
                <SelectTrigger id="shortage-reason-select" className="mt-1" data-testid="shortage-reason-select">
                  <SelectValue placeholder="Pick a reason…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="supplier_short_ship">Supplier short-shipped</SelectItem>
                  <SelectItem value="damaged_on_arrival">Damaged on arrival</SelectItem>
                  <SelectItem value="lost_in_transit">Lost in transit</SelectItem>
                  <SelectItem value="cancelled_by_supplier">Cancelled by supplier</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline" htmlFor="shortage-notes-text">Notes <span className="font-normal lowercase tracking-normal text-muted-foreground">(optional)</span></Label>
              <textarea
                id="shortage-notes-text"
                rows={3}
                value={shortageNotes}
                onChange={(e) => setShortageNotes(e.target.value)}
                placeholder="e.g. Pallet 3 of 5 missing on arrival — claim raised with carrier 2026-02-15."
                className="mt-1 w-full text-sm p-2 border border-border bg-background rounded-none resize-y"
                data-testid="shortage-notes-text"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShortageOpen(false)}
                disabled={shortageSubmitting}
                className="flex-1 rounded-none"
                data-testid="shortage-cancel-btn"
              >
                Keep receiving
              </Button>
              <Button
                onClick={submitShortage}
                disabled={shortageSubmitting || !shortageReason}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white rounded-none"
                data-testid="shortage-confirm-btn"
              >
                {shortageSubmitting ? 'Closing…' : 'Close with shortage'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!unknownBarcode} onOpenChange={(v) => { if (!v) { setUnknownBarcode(null); setScannerOpen(true); } }}>
        <DialogContent className="border border-border rounded-md" data-testid="match-unknown-dialog">
          <DialogHeader>
            <DialogTitle>Unknown barcode — match to a variant</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Scanned <span className="font-mono font-bold text-foreground">{unknownBarcode?.barcode}</span>{' '}
              — pick which variant this barcode belongs to. Future scans will land on it automatically.
            </p>
            <div>
              <Label className="overline">Match to variant</Label>
              <Select value={matchPickVariantId} onValueChange={setMatchPickVariantId}>
                <SelectTrigger className="mt-1" data-testid="match-variant-select"><SelectValue placeholder="Pick a variant…" /></SelectTrigger>
                <SelectContent>
                  {variants.map((v) => {
                    const sku = skusById[v.sku_id];
                    return (
                      <SelectItem key={v.id} value={v.id}>
                        {sku?.sku_code || v.barcode}
                        {v.size ? ` · ${v.size}` : ''}{v.color ? ` · ${v.color}` : ''}
                        {sku?.name ? ` — ${sku.name}` : ''}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => { setUnknownBarcode(null); setScannerOpen(true); }}
                className="flex-1 h-11"
                data-testid="match-skip-btn"
              >Skip</Button>
              <Button
                onClick={confirmUnknownMatch}
                disabled={!matchPickVariantId}
                className="flex-1 h-11 bg-brand hover:bg-brand/90"
                data-testid="match-confirm-btn"
              >Match &amp; receive +1</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Batch put-away preview dialog. Opens from the "Auto-allocate all"
          button in the Expected Items panel; shows one row per unreceived
          line with the best-fit suggestion + an editable location dropdown.
          Confirm sequentially calls receive-item for each line. */}
      <Dialog open={batchOpen} onOpenChange={(v) => { if (!v && !batchRunning) setBatchOpen(false); }}>
        <DialogContent className="border border-border rounded-none max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="batch-putaway-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-mono">
              <Robot size={20} weight="bold" />
              Auto-allocate · {batchPlan.length} line{batchPlan.length === 1 ? '' : 's'}
            </DialogTitle>
          </DialogHeader>
          {batchPreparing ? (
            <div className="py-10 text-center text-sm text-muted-foreground">Computing best-fit per line…</div>
          ) : batchPlan.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">No lines to allocate.</div>
          ) : (
            <>
              <div className="text-xs text-muted-foreground mb-2">
                Each row shows the suggested put-away bin. Override the location dropdown if needed; lines flagged "no fit" must be picked manually or removed (uncheck).
              </div>
              <div className="overflow-auto border border-border flex-1">
                <table className="w-full text-xs">
                  <thead className="bg-muted sticky top-0 z-10">
                    <tr>
                      <th className="text-left p-2">SKU</th>
                      <th className="text-left p-2">Description</th>
                      <th className="text-right p-2">Qty</th>
                      <th className="text-left p-2">UOM</th>
                      <th className="text-left p-2">Put-away</th>
                      <th className="text-center p-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchPlan.map((row) => {
                      const isConsolidation = row.best?.consolidation;
                      const skuForRow = (() => {
                        const v = variants.find((x) => x.id === row.variant_id);
                        return v && skusById[v.sku_id];
                      })();
                      return (
                        <tr key={row.variant_id} className="border-t border-border" data-testid={`batch-row-${row.code}`}>
                          <td className="p-2 font-mono">{row.code}</td>
                          <td className="p-2 truncate max-w-[200px]">{row.description || '—'}</td>
                          <td className="p-2 text-right tabular-nums">{row.qty}</td>
                          <td className="p-2">
                            <UomPicker
                              sku={skuForRow}
                              value={row.uom || (skuForRow?.unit_of_measure || '').toLowerCase()}
                              onChange={(code) => setBatchPlan((prev) => prev.map((p) =>
                                p.variant_id === row.variant_id ? { ...p, uom: code } : p))}
                              disabled={batchRunning}
                              testId={`batch-uom-${row.code}`}
                              className="w-full"
                            />
                          </td>
                          <td className="p-2">
                            {row.no_fit ? (
                              <select
                                disabled={batchRunning}
                                value={row.location_id}
                                onChange={(e) => setBatchPlan((prev) => prev.map((p) => p.variant_id === row.variant_id ? { ...p, location_id: e.target.value, no_fit: !e.target.value } : p))}
                                data-testid={`batch-location-${row.code}`}
                                className="w-full text-xs border border-amber-300 bg-amber-50 px-1 py-0.5"
                              >
                                <option value="">— pick manually —</option>
                                {locations.map((loc) => (
                                  <option key={loc.id} value={loc.id}>{loc.location_code}</option>
                                ))}
                              </select>
                            ) : (
                              <select
                                disabled={batchRunning}
                                value={row.location_id}
                                onChange={(e) => setBatchPlan((prev) => prev.map((p) => p.variant_id === row.variant_id ? { ...p, location_id: e.target.value } : p))}
                                data-testid={`batch-location-${row.code}`}
                                className={`w-full text-xs border px-1 py-0.5 ${
                                  isConsolidation ? 'border-emerald-300 bg-emerald-50 text-emerald-900' : 'border-blue-300 bg-blue-50 text-blue-900'
                                }`}
                              >
                                {locations.map((loc) => (
                                  <option key={loc.id} value={loc.id}>{loc.location_code}</option>
                                ))}
                              </select>
                            )}
                          </td>
                          <td className="p-2 text-center text-[10px] uppercase tracking-widest font-bold">
                            {row.error ? <span className="text-rose-700">{row.error}</span> :
                             row.no_fit ? <span className="text-amber-700">No fit</span> :
                             isConsolidation ? <span className="text-emerald-700">Consolidate</span> :
                             <span className="text-blue-700">Empty rack</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {batchRunning && (
                <div className="mt-3 flex items-center gap-2 text-xs">
                  <Progress value={batchProgress.total > 0 ? (batchProgress.done / batchProgress.total) * 100 : 0} className="h-2 flex-1" />
                  <span className="font-mono text-muted-foreground">{batchProgress.done}/{batchProgress.total}</span>
                </div>
              )}
              <div className="mt-3 flex items-center justify-between gap-2">
                <div className="text-[11px] text-muted-foreground">
                  {batchPlan.filter((p) => !p.no_fit && p.location_id).length} of {batchPlan.length} ready ·
                  {' '}{batchPlan.filter((p) => p.no_fit).length} no-fit
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setBatchOpen(false)}
                    disabled={batchRunning}
                    data-testid="batch-putaway-cancel"
                  >Cancel</Button>
                  <Button
                    onClick={runBatchPutaway}
                    disabled={batchRunning || batchPlan.every((p) => p.no_fit || !p.location_id)}
                    className="bg-foreground text-background hover:opacity-90"
                    data-testid="batch-putaway-confirm"
                  >
                    {batchRunning
                      ? `Running ${batchProgress.done}/${batchProgress.total}…`
                      : `Confirm & receive ${batchPlan.filter((p) => !p.no_fit && p.location_id).length} line(s)`}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
